import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import SearchBarNav from './SearchBarNav';
import { useNavigate } from "react-router-dom";
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isEducationOpen, setIsEducationOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const location = useLocation();

  const navRef = useRef(null);
  const buttonRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchToggleRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 👇 close menu if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideMobileSearch =
        mobileSearchRef.current && mobileSearchRef.current.contains(event.target);
      const clickedOnSearchToggle =
        mobileSearchToggleRef.current &&
        mobileSearchToggleRef.current.contains(event.target);

      if (
        isMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }

      if (isMobileSearchOpen && !clickedInsideMobileSearch && !clickedOnSearchToggle) {
        setIsMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, isMobileSearchOpen]);

  useEffect(() => {
    setIsMobileSearchOpen(false);
  }, [location.pathname, location.search]);

  const toggleMenu = () => {
    setIsMobileSearchOpen(false);
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsEducationOpen(false);
  };

  const toggleEducation = () => {
    setIsEducationOpen(!isEducationOpen);
  };

  const closeEducation = () => {
    setIsEducationOpen(false);
  };

  /** Scroll to top when using main nav (including same-route clicks, e.g. Home while on Home). */
  const scrollNavToTop = () => {
    window.scrollTo(0, 0);
  };

  const { user, logout } = useAuth();
 
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const isSearchResultsPage = location.pathname === '/search-results';
  const shouldShowMobileHeaderSearch = !isHomePage && !isSearchResultsPage;

  const handleSearch = (query) => {
    if (query.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(query)}`);
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    if (!mobileSearchQuery.trim()) return;
    navigate(`/search-results?q=${encodeURIComponent(mobileSearchQuery.trim())}`);
    setIsMobileSearchOpen(false);
  };

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <Link
            to="/"
            className="logo"
            onClick={() => {
              closeMenu();
              scrollNavToTop();
            }}
          >
            <div className="logo-icon">
              <img
                src={`${process.env.PUBLIC_URL}/images/logo10.png`}
                alt=""
                className="logo-img"
                width={60}
                height={60}
                decoding="async"
              />
            </div>
            <div className="logo-text">
              <h1>PaisaWise</h1>
              <span className="logo-subtitle">Where Money Meets Wisdom</span>
            </div>
          </Link>

          {/* attach ref to nav */}
          <nav ref={navRef} className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
            <ul className="nav-list">
               <li className="nav-item">
                 <Link
                      to="/"
                      className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
                      onClick={() => {
                        closeMenu();
                        scrollNavToTop();
                      }}
                    >
                      <i className="fas fa-home"></i>
                      Home
                    </Link>
                  </li>
                  <li className="nav-item dropdown">
                    <span 
                      className={`nav-link dropdown-toggle ${location.pathname.includes('/category/') ? 'active' : ''}`}
                      onClick={toggleEducation}
                    >
                      <i className="fas fa-chart-line"></i>
                      Learning
                      <i className={`fas fa-chevron-down ${isEducationOpen ? 'rotated' : ''}`}></i>
                    </span>
                    <ul className={`dropdown-menu ${isEducationOpen ? 'show' : ''}`}>
                      <li className="dropdown-group" aria-label="Financial Planning">
                        <Link
                          to="/category/financial-planning"
                          className="dropdown-group-label"
                          onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                        >
                          Financial Planning
                        </Link>
                        <ul className="dropdown-group-links">
                          <li>
                            <Link
                              to="/category/financial-planning#retirement"
                              onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                            >
                              Retirement
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/category/financial-planning#loan"
                              onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                            >
                              Loan
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/category/financial-planning#miscellaneous"
                              onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                            >
                              Miscellaneous
                            </Link>
                          </li>
                        </ul>
                      </li>
                      <li className="dropdown-group" aria-label="Investment Strategies">
                        <Link
                          to="/category/investment-strategies"
                          className="dropdown-group-label"
                          onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                        >
                          Investment Strategies
                        </Link>
                        <ul className="dropdown-group-links">
                          <li>
                            <Link
                              to="/category/investment-strategies#mutual-funds"
                              onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                            >
                              Mutual Funds
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/category/investment-strategies#miscellaneous"
                              onClick={() => { closeMenu(); closeEducation(); scrollNavToTop(); }}
                            >
                              Miscellaneous
                            </Link>
                          </li>
                        </ul>
                      </li>
                      
                      {/* <li><Link to="/category/tax-planning" onClick={() => { closeMenu(); closeEducation(); }}>Tax Planning</Link></li>
                      <li><Link to="/category/estate-planning" onClick={() => { closeMenu(); closeEducation(); }}>Estate Planning</Link></li> */}
                    </ul>
                  </li>
                  {/* <li className="nav-item dropdown">
                    <span className="nav-link dropdown-toggle">
                      <i className="fas fa-paper-plane"></i>
                      Tools
                      <i className="fas fa-chevron-down"></i>
                    </span>
                    <ul className="dropdown-menu">
                      <li><Link to="/category/market-analysis" onClick={closeMenu}>Market Analysis</Link></li>
                      <li><Link to="/category/economic-insights" onClick={closeMenu}>Economic Insights</Link></li>
                      <li><Link to="/category/global-markets" onClick={closeMenu}>Global Markets</Link></li>
                      <li><Link to="/category/alternative-investments" onClick={closeMenu}>Alternative Investments</Link></li>
                    </ul>
                  </li> */}
                  <li className="nav-item dropdown">
                    <Link
                      to="/tools"
                      className={`nav-link dropdown-toggle ${
                        location.pathname === '/tools' ||
                        location.pathname.startsWith('/calculator/')
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => { closeMenu(); scrollNavToTop(); }}
                    >
                      <i className="fas fa-lightbulb"></i>
                      Tools
                      <i className="fas fa-chevron-down"></i>
                    </Link>
                    <ul className="dropdown-menu">
                      <li className="dropdown-group" aria-label="Tools">
                        <ul className="dropdown-group-links">
                          <li>
                            <Link
                              to="/tools#retirement"
                              onClick={() => {
                                closeMenu();
                                scrollNavToTop();
                              }}
                            >
                              Retirement Planning
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/tools#loan"
                              onClick={() => {
                                closeMenu();
                                scrollNavToTop();
                              }}
                            >
                              Loan
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/tools#investment"
                              onClick={() => {
                                closeMenu();
                                scrollNavToTop();
                              }}
                            >
                              Investment
                            </Link>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </li>              
                  {/* <li className="nav-item">
                    <Link 
                      to="/about" 
                      className={`nav-link ${location.pathname.includes('/about') ? 'active' : ''}`}
                      onClick={closeMenu}
                    >
                      <i className="fas fa-users"></i>
                      About
                    </Link>
                  </li> */}
            </ul>
          </nav>

          <div className="header-actions">
            {/* <button className="search-toggle" title="Search">
              <i className="fas fa-search"></i>
            </button> */}
            <SearchBarNav onSearch={handleSearch} />
            {shouldShowMobileHeaderSearch && (
              <button
                ref={mobileSearchToggleRef}
                type="button"
                className={`mobile-search-toggle ${isMobileSearchOpen ? 'active' : ''}`}
                aria-label={isMobileSearchOpen ? 'Close search' : 'Open search'}
                aria-expanded={isMobileSearchOpen}
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsMobileSearchOpen((prev) => !prev);
                }}
              >
                <i
                  className={`fas ${isMobileSearchOpen ? 'fa-times' : 'fa-search'}`}
                  aria-hidden="true"
                ></i>
              </button>
            )}

            {/* {user ? (
              <>
                <button className="notifications-toggle" title="Notifications">
                  <i className="fas fa-bell"></i>
                  <span className="notification-badge">3</span>
                </button>
              </>
            ) : null} */}

            {/* User Dropdown */}
            <div
              className="users-dropdown"
              onMouseEnter={() => setIsUserMenuOpen(true)}
              onMouseLeave={() => setIsUserMenuOpen(false)}
            >
              {/* User icon click → direct redirect */}
              <Link
                to={user ? "#" : `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
                className="users-toggle"
                title="User"
              >
                <i className="fas fa-circle-user"></i>
              </Link>

              {/* Dropdown menu */}
              <ul className={`submenu ${isUserMenuOpen ? "open" : ""}`}>
                {user ? (
                  <>
                    <li>
                      <Link to="/profile" className="submenu-link">Profile</Link>
                    </li>
                    <li>
                      <button onClick={logout} className="submenu-link">Logout <i className="fas fa-chevron-right"></i></button>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <Link to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} className="submenu-link">Login</Link>
                    </li>
                    <li>
                      <Link to={`/register?redirect=${encodeURIComponent(location.pathname + location.search)}`} className="submenu-link">Register</Link>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Mobile Menu */}
            <button
              ref={buttonRef}
              className="mobile-menu-btn"
              onClick={toggleMenu}
            >
              <span className={`hamburger ${isMenuOpen ? "open" : ""}`}></span>
              <span className={`hamburger ${isMenuOpen ? "open" : ""}`}></span>
              <span className={`hamburger ${isMenuOpen ? "open" : ""}`}></span>
            </button>
          </div>

        </div>
      </div>
      {shouldShowMobileHeaderSearch && (
        <div
          ref={mobileSearchRef}
          className={`mobile-search-panel ${isMobileSearchOpen ? 'open' : ''}`}
        >
          <form onSubmit={handleMobileSearchSubmit} className="mobile-search-form">
            <input
              type="text"
              className="mobile-search-input"
              placeholder="Search articles and tools..."
              value={mobileSearchQuery}
              onChange={(e) => setMobileSearchQuery(e.target.value)}
              autoComplete="off"
            />
            <button type="submit" className="mobile-search-submit" aria-label="Search">
              <i className="fas fa-search" aria-hidden="true"></i>
            </button>
          </form>
        </div>
      )}
    </header>
  );
};

export default Header;
